import * as Sentry from '@sentry/react';
import { scrubBreadcrumb, scrubEvent, scrubRecordingEvent } from './pii-scrub';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENV = import.meta.env.MODE; // 'development' | 'production'
const isProd = ENV === 'production';

export function initSentry() {
  if (!DSN) return;

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,

    // No patient data in Sentry: every event, span, breadcrumb and replay
    // event is scrubbed (pii-scrub.ts), and the user is sent as an id only.
    sendDefaultPii: false,

    // Capture 100% of transactions in dev, 10% in production
    tracesSampleRate: isProd ? 0.1 : 1.0,

    // Session Replay only for sessions that hit an error. The free plan's
    // replay quota is small; 10% of all sessions used it up on sessions where
    // nothing went wrong, leaving none for the ones with a crash.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // mask PII in replays
        maskAllInputs: true,
        blockAllMedia: true,
        // No request or response bodies or headers in replays.
        networkDetailAllowUrls: [],
        beforeAddRecordingEvent: (event) => scrubRecordingEvent(event),
      }),
    ],

    // Don't send events for these noisy non-bugs
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured with value: undefined',
      /^Network Error$/,
      /^Request aborted$/,
    ],

    beforeSend(event) {
      // Drop chunk-load errors — AppErrorBoundary already handles recovery
      const msg = event.exception?.values?.[0]?.value ?? '';
      if (
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Loading chunk \d+ failed/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg)
      ) {
        return null;
      }
      return scrubEvent(event);
    },
    beforeSendTransaction: (event) => scrubEvent(event),
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  });
}

export { Sentry };
